import { SendIntent } from 'send-intent'
import { Capacitor } from '@capacitor/core'
import bus from './eventbus'
import router from './router'
import { classifyShare } from '@/utils/share-classify'
import { ADD_ROUTES, ADD_QUERY, ResourceType } from '@/utils/constants'

// Share intents land on the add screen, which auto-submits to create the item.
// The allowTypeChange query is vestigial (the bookmark<->note toggle was removed)
// but kept harmless as plumbing.
const addRouteWithTypeChange = type => ({
  path: ADD_ROUTES[type],
  query: { [ADD_QUERY.ALLOW_TYPE_CHANGE]: 'true' },
})

// Strip zero-width / invisible chars some Android share sources inject.
function cleanText(value) {
  if (!value) return ''
  return value.replace(/[\u200B-\u200D\uFEFF]/g, '').trim()
}

// Android's SendIntent plugin maps EXTRA_TEXT to `url` for text/plain shares
// (node_modules/send-intent/.../SendIntent.java), so the actual selected text or
// pasted link often lands in result.url. Gather the primary text blob from the
// populated fields in priority order; classifyShare decides bookmark vs note.
function pickShareText(result) {
  return cleanText(
    result.content
    || result.text
    || result.url
    || result.additionalItems?.[0]?.text
    || result.additionalItems?.[0]?.url
    || result.title
    || '',
  )
}

// EXTRA_SUBJECT (result.title) carries the page title on Android.
function pickPageTitle(result) {
  return cleanText(result.title || result.additionalItems?.[0]?.title || '') || null
}

function resolveBookmarkUrl(url) {
  try {
    return new URL(url).href
  } catch {
    try {
      return new URL(decodeURIComponent(url)).href
    } catch {
      return url
    }
  }
}

function dispatchIntent(app, intent) {
  app.config.globalProperties.$store.dispatch('native/onNewIntent', intent)
}

async function checkIntent(app) {
  console.log('Checking Intent')

  try {
    const result = await SendIntent.checkSendIntentReceived()
    if (!result) {
      console.error('No SendIntent found')
      return
    }

    console.log('Intent fields:', {
      url: result.url,
      text: result.text,
      title: result.title,
      type: result.type,
      additionalItems: result.additionalItems,
    })

    const text = pickShareText(result)
    const pageTitle = pickPageTitle(result)

    if (!text) {
      console.error('Empty share intent', result)
      bus.emit('toast:error', 'Could not process shared content')
      return
    }

    const decision = classifyShare({ text, pageTitle })

    const finish = () => {
      SendIntent.finish()
      dispatchIntent(app, null)
    }

    if (decision.kind === 'bookmark') {
      const url = resolveBookmarkUrl(decision.url)
      console.log('Share classified as bookmark:', url)
      dispatchIntent(app, {
        url,
        result: { url, title: decision.title ?? undefined, type: 'text/plain' },
        finish,
      })
      router.replace(addRouteWithTypeChange(ResourceType.BOOKMARK)).catch(() => {})
      return
    }

    console.log('Share classified as note')
    dispatchIntent(app, {
      type: 'note',
      title: decision.title,
      contentHtml: decision.contentHtml,
      result: {
        ...result,
        title: decision.title,
        contentHtml: decision.contentHtml,
        type: 'text/plain',
      },
      finish,
    })
    router.replace(addRouteWithTypeChange(ResourceType.NOTE)).catch(() => {})
  } catch (error) {
    console.error('Error handling sendIntent:', error, error.message)
    bus.emit('toast:error', 'Could not process shared content')
  }
}

bus.$once('app:created', async app => {
  // iOS shares are handled in store/modules/native/index.js via the appUrlOpen
  // listener — more reliable than SendIntent's notification → bridge.eval →
  // window-event chain on iOS 26+. Keep this path for Android (native Intents).
  if (app.config.globalProperties.$isNative && Capacitor.getPlatform() !== 'ios') {
    checkIntent(app)

    window.addEventListener('sendIntentReceived', async () => {
      console.log('sendIntent received')
      checkIntent(app)
    })
  }
})
